import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchProperties,
  saveProperty,
  deleteProperty,
  updateProperty,
} from 'src/store/actions/properties';
import { selectProperties } from 'src/store/selectors/properties';
import Heading from '../../components/Heading';
import PropertiesTable from '../../components/PropertiesTable';
import Tabs from '../../components/Tabs';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { setSearch, clearSearch } from 'src/store/actions/search';
import { upload } from 'src/helpers/upLoadHelper';
import { clearServiceRequests } from 'src/store/actions/serviceRequests';
import { fetchSuiteAreas, clearSuites } from 'src/store/actions/suites';
import { clearFaqsNotesDocuments } from 'src/store/actions/faqsNotesDocuments';
import { fetchItems } from 'src/store/actions/items';
import { selectItems } from 'src/store/selectors/items';
import { selectPRLoader } from 'src/store/selectors/PropertyLoader';
import { variables } from 'src/Tenants';
import {
  fetchtTabList,
  clearSelectedModulePermission,
} from 'src/store/actions/tabSelections';
import { stopLoaders } from 'src/store/actions/loader';
import { clearActiveTab } from 'src/store/actions/activeTab';
import WithNavigation from 'src/layouts/WithNavigation/WithNavigation';

const Properties: React.FC = () => {
  const dispatch = useDispatch();

  const user = useSelector(selectAuthenticatedUser);
  const items = useSelector(selectItems);
  const loader = useSelector(selectPRLoader);
  const tabSelections = useSelector((state: any) => state.tabSelections);

  const reduxProperties = useSelector(selectProperties);
  const [properties, setProperties] = useState<any[]>([]);

  /* PR loader (redux) starts false and only flips true after fetchProperties
   * dispatches in the mount effect. Without this guard, the empty-state
   * splash would flash on first paint before redux flagged loading. */
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);

  useEffect(() => {
    if (loader) {
      setIsInitialLoading(false);
    }
  }, [loader]);

  const isLoadingProperties: boolean = loader || isInitialLoading;

  useEffect(() => {
    dispatch(clearActiveTab());
  }, []);

  useEffect(() => {
    setProperties(reduxProperties);
  }, [reduxProperties]);

  const handleWalkthroughSyncToggle = useCallback(
    (propertyId: string, newSuspendedState: boolean) => {
      const updatedProperties: any[] = properties.map((property: any) => {
        return property.id === propertyId
          ? { ...property, syncAutomationSuspended: newSuspendedState }
          : property;
      });

      setProperties(updatedProperties);
    },
    [properties]
  );

  useEffect(() => {
    dispatch(stopLoaders());
    dispatch(clearServiceRequests());
    dispatch(clearSelectedModulePermission());
    dispatch(clearSearch());
    dispatch(clearSuites());
    dispatch(clearFaqsNotesDocuments());
    dispatch(fetchtTabList());
    dispatch(fetchProperties(user.id, user.roles[0].role.name) as any);
    dispatch(fetchSuiteAreas() as any);

    if (items.length === 0) {
      dispatch(fetchItems() as any);
    }
  }, []);

  const savePropertyHandler = async (data: any) => {
    let params: any = {};

    let imageUrl = null;

    if (data.selectedImage && data.selectedImage.length > 0) {
      imageUrl = await upload(data.selectedImage[0]);
    }

    let stages =
      data.constructionStages && data.constructionStages.length > 0
        ? data.constructionStages.filter((cs: any) => !cs.new)
        : [];

    if (data.propertyId) {
      params = {
        id: data.propertyId,
        propertyId: data.propertyId,
        name: data.name,
        addressLine1: data.addressLine1,
        addressLine2: '',
        city: data.city,
        province: data.province,
        postalCode: data.postalCode,
        latitude: '17.5896',
        longitude: '17.556896',
        image: imageUrl != null ? imageUrl : data.image,
        UserId: user.id,
        PropertyManagerId:
          data.propertyManager && data.propertyManager.id
            ? data.propertyManager.id
            : data.propertyManagerId,
        StrataManagerId: data.strataManagerId
          ? data.strataManagerId
          : data.strataManager && data.strataManager.id
            ? data.strataManager.id
            : null,
        ConstructionStages: stages,
        modulePermissions: data.modulePermissions || [],
      };

      dispatch(updateProperty(params) as any);
    } else {
      params = {
        Name: data.name,
        AddressLine1: data.addressLine1,
        AddressLine2: '',
        City: data.city,
        Province: data.province,
        PostalCode: data.postalCode,
        Latitude: '17.5896',
        Longitude: '17.556896',
        Image: imageUrl,
        UserId: user.id,
        PropertyManagerId: data.propertyManagerId,
        StrataManagerId: data.strataManagerId,
        ConstructionStages: stages,
        modulePermissions: data.modulePermissions || [],
      };

      dispatch(saveProperty(params) as any);
    }
  };

  const deletePropertyHandler = (data: any) => {
    dispatch(deleteProperty(data.id, user.id) as any);
  };

  let tabList = tabSelections.tabList;
  let suites = 0;
  let serviceRequestsCount = 0;

  for (var i = 0; i < properties.length; i++) {
    suites = properties[i].suits + suites;
    serviceRequestsCount = properties[i].serviceRequests + serviceRequestsCount;
  }

  return (
    <WithNavigation
      title="Properties"
      top={
        <Heading
          headline="Properties"
          loading={isLoadingProperties}
          items={[
            {
              key: 'Properties',
              value: properties.length,
            },
            {
              key: variables.suites,
              value: suites,
            },
            {
              key: 'Service Requests',
              value: serviceRequestsCount,
            },
          ]}
        />
      }
      bottom={
        <Tabs
          items={[
            {
              label: 'Properties',
              length: properties.length,
              content: (
                <PropertiesTable
                  properties={properties}
                  isLoadingProperties={isLoadingProperties}
                  onFetchProperties={(userId: any, role: any) =>
                    dispatch(fetchProperties(userId, role) as any)
                  }
                  onSaveProperty={(data: any) => savePropertyHandler(data)}
                  onSearch={(e: any) =>
                    dispatch(setSearch({ properties: e.target.value.trim() }))
                  }
                  onDeleteProperty={(data: any) =>
                    deletePropertyHandler(data)
                  }
                  tabList={tabList}
                  clearSelectedModulePermission={() =>
                    dispatch(clearSelectedModulePermission())
                  }
                  onWalkthroughSyncToggle={handleWalkthroughSyncToggle}
                />
              ),
            },
          ]}
        />
      }
    />
  );
};

export default Properties;
